import { useNavigate } from "react-router-dom";

/** ---------------- CONTENT ONLY (copy/data) ---------------- */
const CONTENT = {
  jobsuite: {
    badge: "AI Interview Prep",
    heroTitle: "Prepare for interviews as easily as starting a conversation",
    heroSub:
      "Simply enter the position you’re applying for and use AI-generated questions and sample answers to ease your next interview.",
    heroImg:
      "https://c.animaapp.com/mge1sasxzrNszH/img/image-60.png",
    sec1Title: "Practice more efficiently and confidently",
    sec1Features: [
      {
        title: "Tailored Questions",
        desc: "Get customized interview questions based on your role, skills, and experience.",
      },
      {
        title: "Relevant Answers",
        desc: "Receive AI-generated example answers crafted for your specific job position.",
      },
      {
        title: "Diverse Scenarios",
        desc: "Explore technical, HR, and behavioral rounds to prepare comprehensively.",
      },
    ],
    sec1CTA: {
      title: "Continuous Improvement",
      desc: "Learn from curated feedback and updated questions reflecting current hiring trends.",
    },
    sec2Title: "Focus on key topics for any position",
    sec2Sub:
      "Master essential areas every interviewer evaluates — from technical expertise to communication and problem-solving.",
    sec2Cards: [
      {
        title: "Core Concepts",
        desc: "Learn fundamentals of your domain from algorithms to design principles.",
        example: 'Example: "Explain supervised vs. unsupervised learning"',
      },
      {
        title: "Problem Solving",
        desc: "Practice real-world questions that assess analytical thinking.",
        example:
          'Example: "How would you debug a model performing poorly in production?"',
      },
      {
        title: "Behavioral & Soft Skills",
        desc: "Prepare STAR-structured answers for situational/HR prompts.",
        example: 'Example: "Describe a time you resolved a team conflict."',
      },
      {
        title: "Latest Trends & Tools",
        desc: "Stay current on frameworks and industry best practices.",
        example: 'Example: "What are the ethical challenges in generative AI?"',
      },
      {
        title: "Communication",
        desc: "Structure clear, confident, concise answers.",
        example:
          'Example: "How would you explain your project to a non-technical audience?"',
      },
    ],
  },

  studysuite: {
    badge: "AI Study Abroad",
    heroTitle: "Plan, practice, and apply—your study abroad copilot",
    heroSub:
      "Get AI course suggestions, realistic visa/university mock interviews, and polished SOP/LOR/CV to stand out.",
    heroImg:
      "https://c.animaapp.com/mge1sasxzrNszH/img/image-60.png", // swap if you have a study image
    sec1Title: "Prepare smarter for admissions & visa",
    sec1Features: [
      {
        title: "AI Course Suggestions",
        desc: "Programs matched to your profile, budget, country choice, and goals.",
      },
      {
        title: "Visa & University Mocks",
        desc: "Speech/text simulations with feedback on intent, finances, and ties.",
      },
      {
        title: "Document Builder",
        desc: "SOP/LOR/CV/Cover drafts with originality, tone, and word-limit checks.",
      },
    ],
    sec1CTA: {
      title: "Application Readiness",
      desc: "Track deadlines, intakes, and checklists with iterative feedback to improve admits.",
    },
    sec2Title: "What you’ll master for successful admits",
    sec2Sub:
      "Cover the pillars universities and visa officers evaluate—fit, clarity of goals, finances, academics, and communication.",
    sec2Cards: [
      {
        title: "Program–Profile Fit",
        desc: "Align background, goals, and program outcomes with evidence.",
        example: 'Example: "Why this program and how it advances your goals?"',
      },
      {
        title: "Visa Readiness",
        desc: "Demonstrate intent to study, financial sufficiency, and home ties.",
        example:
          'Example: "How will you fund your studies and manage living costs?"',
      },
      {
        title: "Strong Documents",
        desc: "Structure compelling SOP and specific, credible LORs.",
        example:
          'Example: "Craft a results-focused SOP paragraph for your capstone."',
      },
      {
        title: "Deadlines & Intakes",
        desc: "Plan intakes and scholarship timelines without last-minute rush.",
        example: 'Example: "Shortlist fall 2026 deadlines for top choices."',
      },
      {
        title: "Clear Communication",
        desc: "Concise, confident, and coherent answers for panels.",
        example:
          'Example: "Explain a project impact to a non-technical officer."',
      },
    ],
  },
};

/**
 * Desktop22
 * @param {"jobsuite"|"studysuite"} suite - content variant (defaults to "jobsuite")
 */
const Desktop22 = ({ suite = "jobsuite" }) => {
  const navigate = useNavigate();
  const S = CONTENT[suite] || CONTENT.jobsuite;

  return (
    <div className="bg-[#f9f9f9] w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#06010A] to-[#260345] text-white flex flex-col md:flex-row items-center justify-between px-8 md:px-16 lg:px-24 py-20 overflow-hidden">
        {/* Decorative Ellipses */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[400px] h-[400px] bg-[#7B3FE4]/40 rounded-full blur-3xl top-[-100px] left-[-100px]" />
          <div className="absolute w-[350px] h-[350px] bg-[#E6C1FF]/30 rounded-full blur-3xl bottom-[-150px] right-[-100px]" />
        </div>

        {/* Left Content */}
        <div className="relative z-10 max-w-xl">
          <h3 className="text-lg text-[#ffffffbf] mb-3">{S.badge}</h3>
          <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-6">
            {S.heroTitle}
          </h1>
          <p className="text-gray-200 mb-8 text-base md:text-lg">
            {S.heroSub}
          </p>

          <button
            onClick={() => navigate("/auth")}
            className="bg-[#ffc403] text-black font-bold px-6 py-3 rounded-xl hover:bg-[#e6b800] transition"
          >
            Get Started
          </button>
        </div>

        {/* Right Image */}
        <div className="relative z-10 mt-12 md:mt-0">
          <img
            src={S.heroImg}
            alt="Preview"
            className="w-[350px] md:w-[450px] object-contain"
          />
        </div>
      </section>

      {/* Section 1 */}
      <section className="bg-[#f9f9f9] py-20 px-6 md:px-16 lg:px-24 text-center text-[#22033c] relative">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">
          {S.sec1Title}
        </h2>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-12 max-w-6xl mx-auto">
          {S.sec1Features.map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center space-y-3">
              <h3 className="font-semibold text-lg md:text-xl">{item.title}</h3>
              <p className="text-base text-[#22033c]/80 max-w-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA / Continuous Improvement */}
        <div className="mt-16 flex flex-col items-center text-center space-y-3 max-w-lg mx-auto">
          <h3 className="font-semibold text-lg md:text-xl">{S.sec1CTA.title}</h3>
          <p className="text-base text-[#22033c]/80">{S.sec1CTA.desc}</p>
        </div>
      </section>

      {/* Section 2 */}
      <section className="bg-[#f9f9f9] py-20 px-6 md:px-16 lg:px-24 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold text-[#22033c] mb-4">
          {S.sec2Title}
        </h2>
        <p className="text-[#22033c]/80 text-lg mb-10 max-w-3xl mx-auto">
          {S.sec2Sub}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
          {S.sec2Cards.map((item, i) => (
            <div
              key={i}
              className="bg-gradient-to-b from-[#1a012d] to-[#3b0a6e] text-white rounded-xl shadow-md p-6 flex flex-col text-left space-y-3"
            >
              <h3 className="font-semibold text-lg">{item.title}</h3>
              <p className="text-gray-200 text-sm">{item.desc}</p>
              <p className="text-[#e6c1ff] text-xs italic">{item.example}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer (optional) */}
      {/* <Footer /> */}
    </div>
  );
};

export default Desktop22;
